import React, { useMemo, useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { CBadge } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilChevronRight, cilLockLocked } from '@coreui/icons'
import { usePermission } from '../contexts/PermissionContext'

export const AppSidebarNav = ({ items }) => {
  const location = useLocation()
  const { user } = useSelector(state => state.auth)
  const { hasPermission, hasAnyRole } = usePermission()
  const [expandedGroups, setExpandedGroups] = useState({})
  
  // Helper to check if user has any of the specified roles
  const isAnyRole = (roles) => {
    if (!roles || !Array.isArray(roles)) return true
    const userRole = user?.role || user?.Role?.name
    return roles.includes(userRole)
  }
  
  // Filter navigation items based on user permissions
  const filteredItems = useMemo(() => {
    const filterItems = (itemsList) => {
      return itemsList.reduce((acc, item) => {
        // Check if item has role restrictions
        if (item.allowedRoles && item.allowedRoles.length > 0) {
          if (!isAnyRole(item.allowedRoles)) {
            return acc
          }
        }
        
        // Check if item has permission requirements
        if (item.permission) {
          const { resource, action, viewCode, actionCode } = item.permission
          // Support both old (resource/action) and new (viewCode/actionCode) formats
          const view = viewCode || resource
          const act = actionCode || action
          
          if (view && act && !hasPermission(view, act)) {
            return acc
          }
        }
        
        // Handle items with children
        if (item.items && item.items.length > 0) {
          const filteredChildren = filterItems(item.items)
          // Hide group if all children are filtered out
          if (filteredChildren.length === 0) {
            return acc
          }
          // Add item with filtered children
          acc.push({ ...item, items: filteredChildren })
        } else {
          // Add regular item
          acc.push(item)
        }
        
        return acc
      }, [])
    }
    
    return filterItems(items)
  }, [items, hasPermission, isAnyRole])

  // Auto-expand groups that contain the active page - ONLY run when pathname changes
  useEffect(() => {
    const newExpandedGroups = {}
    
    const checkActiveInGroup = (itemsList, parentIndex = '') => {
      itemsList.forEach((item, index) => {
        const groupKey = parentIndex ? `${parentIndex}-${index}` : `${index}`
        
        if (item.items && item.items.length > 0) {
          const hasActivePath = item.items.some(child => 
            child.to && location.pathname.startsWith(child.to)
          )
          
          if (hasActivePath || (item.to && location.pathname.startsWith(item.to))) {
            newExpandedGroups[groupKey] = true
          }
          
          checkActiveInGroup(item.items, groupKey)
        }
      })
    }
    
    checkActiveInGroup(filteredItems)
    setExpandedGroups(newExpandedGroups)
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (groupKey) => {
    console.log('Toggling group:', groupKey, 'Current state:', expandedGroups[groupKey])
    setExpandedGroups(prev => {
      const newState = {
        ...prev,
        [groupKey]: !prev[groupKey]
      }
      console.log('New expanded state:', newState)
      return newState
    })
  }

  const navItem = (item, index, parentIndex) => {
    const { name, badge, icon, to } = item
    const isActive = to && location.pathname === to
    
    return (
      <div key={index} className={`nav-item-custom ${isActive ? 'active' : ''}`}>
        <NavLink to={to || '#'}>
          {icon && (
            <span className="nav-icon">
              {typeof icon === 'string' || typeof icon === 'object' ? (
                <CIcon icon={icon} />
              ) : (
                icon
              )}
            </span>
          )}
          <span className="nav-item-text">{name}</span>
          {badge && (
            <CBadge color={badge.color} className="badge">
              {badge.text}
            </CBadge>
          )}
        </NavLink>
      </div>
    )
  }

  const navGroup = (item, index, parentIndex) => {
    const { name, icon, items: children } = item
    const groupKey = parentIndex ? `${parentIndex}-${index}` : `${index}`
    const isExpanded = expandedGroups[groupKey]
    const hasChildren = children && children.length > 0
    
    // console.log('Rendering group:', name, 'key:', groupKey, 'expanded:', isExpanded, 'children:', children?.length)
    
    return (
      <div key={index} className="nav-group-custom ">
        <button
          type="button"
          className="nav-group-toggle"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            toggleGroup(groupKey)
          }}
          aria-expanded={isExpanded}
        >
          {icon && (
            <span className="nav-icon">
              {typeof icon === 'string' || typeof icon === 'object' ? (
                <CIcon icon={icon} />
              ) : (
                icon
              )}
            </span>
          )}
          <span className="nav-item-text">{name}</span>
          <CIcon 
            icon={cilChevronRight} 
            className={`nav-group-toggle-icon ${isExpanded ? 'expanded' : ''}`}
          />
        </button>
        <div 
          className={`nav-group-items ${isExpanded ? 'expanded' : 'collapsed'}`}
          style={{ display: isExpanded ? 'block' : 'none' }}
        >
          {hasChildren ? (
            children.map((child, childIndex) =>
              child.items 
                ? navGroup(child, childIndex, groupKey)
                : navItem(child, childIndex, groupKey)
            )
          ) : (
            <div 
              className="nav-item-custom nav-no-access"
              style={{
                padding: '0.5rem 1rem',
                opacity: 0.6,
                cursor: 'not-allowed',
                pointerEvents: 'none'
              }}
            >
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <span className="nav-icon" style={{ color: '#999' }}>
                  <CIcon icon={cilLockLocked} size="sm" />
                </span>
                <span className="nav-item-text" style={{ color: '#999', fontStyle: 'italic' }}>
                  No access to any views here
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  const navTitle = (item, index) => {
    return (
      <div key={index} className="nav-title">
        {item.name}
      </div>
    )
  }

  return (
    <nav className="sidebar-nav" aria-label="Main navigation">
      {filteredItems?.map((item, index) => {
        if (item.component?.displayName === 'CNavTitle' || item._component === 'CNavTitle') {
          return navTitle(item, index)
        }
        if (item.items) {
          return navGroup(item, index)
        }
        return navItem(item, index)
      })}
    </nav>
  )
}

AppSidebarNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
}
